// =====================================================================
// Faltosos / No-Show (S112) — lista pra recepção RECUPERAR paciente que
// faltou + sinalizar o desfecho (reagendou / não conseguiu) pra Dra ter
// controle. Read-only da lista (GET /clinic-dashboard/no-shows) + POST do
// desfecho (/no-shows/:id/recovery). Botão "abrir conversa" reusa o
// NewTicketModal. Sem IA. Estilos escopados (makeStyles).
// =====================================================================
import React, { useEffect, useState, useCallback, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Typography,
  CircularProgress,
  Box,
  TextField,
  Chip,
  Tooltip,
  Select,
  MenuItem,
  FormControl
} from "@material-ui/core";
import { WhatsApp, EventBusy, Refresh, PictureAsPdf } from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import moment from "moment";

import api from "../../services/api";
import { getNoShows, setNoShowRecovery } from "../../services/clinicDashboardService";
import NewTicketModal from "../../components/NewTicketModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

// desfechos possíveis (recepção marca)
const RECOVERY = {
  pending: { label: "Pendente", color: "#8b8f9a" },
  contacted: { label: "🔄 Em contato", color: "#c98a00" },
  rescheduled: { label: "✅ Reagendou", color: "#1f9d55" },
  attended: { label: "🙋 Já compareceu", color: "#0d9488" },
  failed: { label: "❌ Não conseguiu", color: "#d64545" }
};
const RECOVERY_KEYS = ["pending", "contacted", "rescheduled", "attended", "failed"];

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    root: {
      padding: theme.spacing(3),
      overflowY: "auto",
      height: "calc(100vh - 48px)",
      background: dark ? "#0e0f13" : "#f4f5f7"
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 12,
      marginBottom: theme.spacing(1)
    },
    title: { fontWeight: 700, fontSize: 24, letterSpacing: "-0.5px", display: "flex", alignItems: "center", gap: 8 },
    subtitle: { color: dark ? "#8b8f9a" : "#6b7280", fontSize: 13, marginTop: 2 },
    filters: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" },
    placar: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: theme.spacing(2) },
    paper: { padding: 0, overflow: "hidden" },
    phone: { fontFamily: "monospace", fontSize: 13 },
    empty: { padding: theme.spacing(6), textAlign: "center", color: dark ? "#8b8f9a" : "#6b7280" },
    countChip: { fontWeight: 700 },
    who: { fontSize: 11, color: dark ? "#8b8f9a" : "#6b7280", marginTop: 2 }
  };
});

const onlyDigits = (s) => String(s || "").replace(/\D/g, "");
const fmtPhone = (s) => {
  const d = onlyDigits(s);
  if (d.length >= 12) {
    const ddd = d.slice(2, 4);
    const rest = d.slice(4);
    return `(${ddd}) ${rest.slice(0, rest.length - 4)}-${rest.slice(-4)}`;
  }
  return s;
};

// rótulo do pagamento (reusado na tabela e no PDF)
const paymentLabel = (it) =>
  it.paymentType === "convenio"
    ? `Convênio${it.insuranceName ? ` (${it.insuranceName})` : ""}`
    : it.paymentType === "cortesia"
    ? "Cortesia"
    : "Particular";

// escape pro HTML do relatório (nome de paciente pode ter <, &, etc.)
const esc = (s) =>
  String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const NoShows = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [to, setTo] = useState(moment().format("YYYY-MM-DD"));

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getNoShows(from, to);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const handleOpenConversation = async (item) => {
    const digits = onlyDigits(item.patientPhone);
    try {
      if (item.patientContactId) {
        setSelectedContact({ id: item.patientContactId, name: item.patientName, number: digits });
        setModalOpen(true);
        return;
      }
      if (digits.length >= 8) {
        const tail = digits.slice(-8);
        const { data } = await api.get("/contacts", { params: { searchParam: tail } });
        const found = (data.contacts || []).find((c) => onlyDigits(c.number).endsWith(tail));
        if (found) {
          setSelectedContact({ id: found.id, name: found.name, number: found.number });
          setModalOpen(true);
          return;
        }
      }
      setSelectedContact(null);
      setModalOpen(true);
    } catch (err) {
      toastError(err);
    }
  };

  const handleCloseModal = (ticket) => {
    setModalOpen(false);
    setSelectedContact(null);
    if (ticket && ticket.uuid) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  // recepção sinaliza o desfecho — atualização otimista + grava no backend
  const handleSetRecovery = async (item, status) => {
    const prev = { s: item.recoveryStatus, by: item.recoveryBy, at: item.recoveryAt };
    setItems((cur) =>
      cur.map((x) =>
        x.id === item.id
          ? { ...x, recoveryStatus: status, recoveryBy: user?.name || "você", recoveryAt: new Date().toISOString() }
          : x
      )
    );
    try {
      await setNoShowRecovery(item.id, status);
    } catch (err) {
      toastError(err);
      setItems((cur) =>
        cur.map((x) =>
          x.id === item.id ? { ...x, recoveryStatus: prev.s, recoveryBy: prev.by, recoveryAt: prev.at } : x
        )
      );
    }
  };

  const placar = items.reduce((a, it) => {
    const k = it.recoveryStatus || "pending";
    a[k] = (a[k] || 0) + 1;
    return a;
  }, {});

  // Baixar PDF — monta um relatório HTML branded e imprime via iframe oculto
  // (navegador → "Salvar como PDF"). Zero dependência nova, sem popup-blocker.
  const handleDownloadPdf = () => {
    if (!items.length) return;
    const clinic = (user && user.company && user.company.name) || "";
    const periodo = `${moment(from).format("DD/MM/YYYY")} a ${moment(to).format("DD/MM/YYYY")}`;
    const geradoEm = moment().format("DD/MM/YYYY HH:mm");
    const placarStr = RECOVERY_KEYS.filter((k) => placar[k])
      .map((k) => `${RECOVERY[k].label}: ${placar[k]}`)
      .join("  ·  ");

    const rows = items
      .map((it) => {
        const st = it.recoveryStatus || "pending";
        const quem =
          st !== "pending" && it.recoveryBy
            ? `por ${esc(it.recoveryBy)}${
                it.recoveryAt ? ` · ${moment(it.recoveryAt).format("DD/MM HH:mm")}` : ""
              }`
            : "";
        return `<tr>
          <td>${esc(it.dataBr)}</td>
          <td>${esc(it.horaBr)}</td>
          <td>${esc(it.patientName)}</td>
          <td class="mono">${esc(fmtPhone(it.patientPhone))}</td>
          <td>${esc(it.professional)}</td>
          <td>${esc(paymentLabel(it))}</td>
          <td>${esc(RECOVERY[st].label)}${quem ? `<br><span class="quem">${quem}</span>` : ""}</td>
        </tr>`;
      })
      .join("");

    const html = `<!doctype html><html lang="pt-br"><head><meta charset="utf-8">
      <title>Faltosos ${esc(periodo)}</title>
      <style>
        * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { font-family: Arial, Helvetica, sans-serif; color: #1a1a1a; margin: 24px; }
        h1 { font-size: 18px; margin: 0 0 2px; }
        .clinic { font-size: 13px; color: #444; margin: 0 0 10px; }
        .meta { font-size: 12px; color: #555; margin: 0 0 4px; }
        .placar { font-size: 12px; color: #333; margin: 6px 0 14px; font-weight: 600; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        th, td { border: 1px solid #d0d0d0; padding: 5px 7px; text-align: left; vertical-align: top; }
        th { background: #f0f0f0; font-size: 11px; }
        .mono { font-family: 'Courier New', monospace; }
        .quem { color: #777; font-size: 10px; }
        tr:nth-child(even) td { background: #fafafa; }
        .foot { margin-top: 12px; font-size: 10px; color: #888; }
        @page { size: A4 landscape; margin: 12mm; }
      </style></head><body>
        <h1>Faltosos (No-Show)</h1>
        ${clinic ? `<div class="clinic">${esc(clinic)}</div>` : ""}
        <div class="meta">Período: ${esc(periodo)} &nbsp;•&nbsp; Total: ${items.length} falta${
      items.length === 1 ? "" : "s"
    } &nbsp;•&nbsp; Gerado em ${esc(geradoEm)}</div>
        ${placarStr ? `<div class="placar">${esc(placarStr)}</div>` : ""}
        <table>
          <thead><tr>
            <th>Data</th><th>Hora</th><th>Paciente</th><th>Telefone</th>
            <th>Médico</th><th>Pagamento</th><th>Situação</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="foot">Documento gerado pelo sistema — uso interno. Contém dados de pacientes (LGPD): não compartilhar.</div>
      </body></html>`;

    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    document.body.appendChild(iframe);
    const cleanup = () => {
      try {
        document.body.removeChild(iframe);
      } catch (_) {}
    };
    const win = iframe.contentWindow;
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.onafterprint = cleanup;
    setTimeout(() => {
      try {
        win.focus();
        win.print();
      } catch (e) {
        toastError(e);
        cleanup();
      }
    }, 300);
    // rede de segurança caso onafterprint não dispare (cancelar em alguns navegadores)
    setTimeout(cleanup, 60000);
  };

  return (
    <div className={classes.root}>
      <NewTicketModal
        modalOpen={modalOpen}
        onClose={handleCloseModal}
        initialContact={selectedContact || undefined}
      />

      <div className={classes.header}>
        <div>
          <Typography className={classes.title}>
            <EventBusy color="error" /> Faltosos (No-Show)
          </Typography>
          <div className={classes.subtitle}>
            Pacientes que faltaram — entre em contato pra recuperar e marque o desfecho.
          </div>
        </div>
        <div className={classes.filters}>
          <TextField label="De" type="date" size="small" value={from}
            onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="Até" type="date" size="small" value={to}
            onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={load} disabled={loading}>
            Atualizar
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PictureAsPdf />}
            onClick={handleDownloadPdf}
            disabled={loading || items.length === 0}
          >
            Baixar PDF
          </Button>
          <Chip className={classes.countChip} color="secondary" label={`${total} falta${total === 1 ? "" : "s"}`} />
        </div>
      </div>

      {/* Placar da recuperação — pra Dra bater o olho */}
      {total > 0 && (
        <div className={classes.placar}>
          {RECOVERY_KEYS.map((k) =>
            placar[k] ? (
              <Chip
                key={k}
                size="small"
                label={`${RECOVERY[k].label}: ${placar[k]}`}
                style={{ background: RECOVERY[k].color, color: "#fff", fontWeight: 600 }}
              />
            ) : null
          )}
        </div>
      )}

      <Paper className={classes.paper}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={6}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <div className={classes.empty}>🎉 Nenhuma falta no período. Ótimo sinal!</div>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Hora</TableCell>
                <TableCell>Paciente</TableCell>
                <TableCell>Telefone</TableCell>
                <TableCell>Médico</TableCell>
                <TableCell>Pagamento</TableCell>
                <TableCell>Situação</TableCell>
                <TableCell align="right">Ação</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it) => {
                const st = it.recoveryStatus || "pending";
                return (
                  <TableRow key={it.id} hover>
                    <TableCell>{it.dataBr}</TableCell>
                    <TableCell>{it.horaBr}</TableCell>
                    <TableCell>{it.patientName}</TableCell>
                    <TableCell className={classes.phone}>{fmtPhone(it.patientPhone)}</TableCell>
                    <TableCell>{it.professional}</TableCell>
                    <TableCell>{paymentLabel(it)}</TableCell>
                    <TableCell>
                      <FormControl size="small" variant="outlined">
                        <Select
                          value={st}
                          onChange={(e) => handleSetRecovery(it, e.target.value)}
                          style={{ minWidth: 148, color: RECOVERY[st].color, fontWeight: 600, fontSize: 13 }}
                        >
                          {RECOVERY_KEYS.map((k) => (
                            <MenuItem key={k} value={k} style={{ color: RECOVERY[k].color, fontWeight: 600 }}>
                              {RECOVERY[k].label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {st !== "pending" && it.recoveryBy ? (
                        <div className={classes.who}>
                          por {it.recoveryBy}
                          {it.recoveryAt ? ` · ${moment(it.recoveryAt).format("DD/MM HH:mm")}` : ""}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Abrir conversa no WhatsApp (CRM)">
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<WhatsApp />}
                          style={{ background: "#25d366", color: "#fff", whiteSpace: "nowrap" }}
                          onClick={() => handleOpenConversation(it)}
                        >
                          Abrir conversa
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>
    </div>
  );
};

export default NoShows;
